import { ReactNode, useMemo } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { SidePanelPropertyKey } from 'src/graphql/entities/side-panel-personalization';
import { CustomAttributesEntity } from './custom-attributes-config/custom-attributes-config';
import { CustomAttributeRowData } from './field-view-registry/types';
import { groupCustomAttributes } from './hooks/group-custom-attributes';
import { useCustomAttributeSaving } from './hooks/use-custom-attribute-saving';
import { useCustomAttributeValues } from './hooks/use-custom-attribute-values';
import { useNormalizedCustomAttributeDefinitions } from './hooks/use-normalized-custom-attribute-definitions';
import {
  CustomAttributesSidePanelContext,
  CustomAttributesSidePanelData,
} from './side-panel-context';

interface CustomAttributesSidePanelProviderProps {
  entity: CustomAttributesEntity;
  entityId: string;
  partnerId: number | undefined;
  canEdit: boolean;
  children: ReactNode;
}

export const CustomAttributesSidePanelProvider = ({
  entity,
  entityId,
  partnerId,
  canEdit,
  children,
}: CustomAttributesSidePanelProviderProps) => {
  const { groupRules } = useGroupRules();
  const canView = Boolean(groupRules[UserGroupRules.AccessToViewCustomAttributes]);

  const {
    visibleDefinitions,
    groups: definedGroups,
    loading: loadingDefinitions,
  } = useNormalizedCustomAttributeDefinitions({ partnerId, entity });

  const {
    parsedValues,
    payloadError,
    loading: loadingValues,
  } = useCustomAttributeValues({ entity, entityId });

  const { setFieldValue, savingKeys, errorByKey } = useCustomAttributeSaving({
    entity,
    entityId,
  });

  const rowsByPropertyKey = useMemo(() => {
    const rows = new Map<SidePanelPropertyKey, CustomAttributeRowData>();

    if (!canView) {
      return rows;
    }

    const groups = groupCustomAttributes({
      definitions: visibleDefinitions,
      definedGroups,
      parsedValues,
      savingKeys,
      errorByKey,
    });

    for (const group of groups) {
      for (const row of group.rows) {
        rows.set(`ca:${row.definition.key}`, row);
      }
    }

    return rows;
  }, [canView, visibleDefinitions, definedGroups, parsedValues, savingKeys, errorByKey]);

  const value = useMemo<CustomAttributesSidePanelData>(
    () => ({
      rowsByPropertyKey,
      loading: canView && (loadingDefinitions || loadingValues),
      payloadError: canView ? payloadError : null,
      disabled: !canEdit || payloadError !== null,
      onSave: setFieldValue,
    }),
    [
      rowsByPropertyKey,
      canView,
      loadingDefinitions,
      loadingValues,
      payloadError,
      canEdit,
      setFieldValue,
    ],
  );

  return (
    <CustomAttributesSidePanelContext.Provider value={value}>
      {children}
    </CustomAttributesSidePanelContext.Provider>
  );
};
